import { FunctionComponent } from 'react';
import { Box, Text } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import { ReactRenderer as Renderer } from '@atlaskit/renderer';
import SectionMessage from '@atlaskit/section-message';
import { useIntl } from 'react-intl-next';

const messages = {
  contentDisplayErrorTItle: {
    id: 'adfContentSection.contentDisplayError',
    defaultMessage: 'Content Display Error',
    description: 'Title for error message when ADF content cannot be displayed',
  },
  contentDisplayErrorDescription: {
    id: 'adfContentSection.contentDisplayErrorDescription',
    defaultMessage:
      'The content for this section could not be displayed due to a formatting issue.',
    description:
      'Description for error message when ADF content cannot be displayed',
  },
};
export interface AdfContentSectionProps {
  /**
   * The ADF document as a JSON string
   */
  content: string;
  /**
   * Optional title for the section
   */
  title?: string;
}

/**
 * Renders ADF (Atlassian Document Format) content within a section.
 * Includes error handling for malformed ADF strings and displays
 * a fallback message if the content cannot be parsed.
 */
export const AdfContentSection: FunctionComponent<AdfContentSectionProps> = ({
  content,
  title,
}) => {
  const { formatMessage } = useIntl();

  if (!content) {
    return null;
  }

  let parsedDocument: any;

  try {
    parsedDocument = JSON.parse(content);
  } catch (error) {
    // Log the error for debugging purposes
    console.error('Failed to parse ADF content:', error);

    return (
      <Box paddingBlockEnd="space.400">
        {title && (
          <Box paddingBlockEnd="space.100">
            <Heading as="h2" size="small">
              {title}
            </Heading>
          </Box>
        )}
        <SectionMessage
          appearance="warning"
          title={formatMessage(messages.contentDisplayErrorTItle)}
        >
          <Text>{formatMessage(messages.contentDisplayErrorDescription)}</Text>
        </SectionMessage>
      </Box>
    );
  }

  return (
    <Box paddingBlockEnd="space.400">
      {title && (
        <Box paddingBlockEnd="space.100">
          <Heading as="h2" size="small">
            {title}
          </Heading>
        </Box>
      )}
      <Renderer document={parsedDocument} />
    </Box>
  );
};

export default AdfContentSection;
